/**
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

/**
 * Haptic Feedback Utility
 * Provides subtle vibration feedback for mobile interactions
 * Respects user preferences and device capabilities
 */

// Haptic feedback types
export enum HapticType {
  LIGHT = 'light', // 10ms - for light taps
  MEDIUM = 'medium', // 20ms - for standard interactions
  HEAVY = 'heavy', // 30ms - for important actions
}

// Haptic duration mapping (in milliseconds)
const HAPTIC_DURATIONS: Record<HapticType, number> = {
  [HapticType.LIGHT]: 10,
  [HapticType.MEDIUM]: 20,
  [HapticType.HEAVY]: 30,
}

// User preference key for localStorage
const HAPTIC_PREFERENCE_KEY = 'noorwriter_haptic_enabled'

/**
 * Check if haptic feedback is supported by the device
 */
export function isHapticSupported(): boolean {
  return 'vibrate' in navigator
}

/**
 * Check if haptic feedback is enabled by user preference
 */
export function isHapticEnabled(): boolean {
  // Check localStorage preference
  const preference = localStorage.getItem(HAPTIC_PREFERENCE_KEY)

  // Default to enabled if no preference is set
  if (preference === null) {
    return true
  }

  return preference === 'true'
}

/**
 * Set user preference for haptic feedback
 */
export function setHapticEnabled(enabled: boolean): void {
  localStorage.setItem(HAPTIC_PREFERENCE_KEY, enabled.toString())
}

/**
 * Check if device is likely a mobile device
 * Haptic feedback is primarily for mobile devices
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
}

/**
 * Trigger haptic feedback
 * @param type - Type of haptic feedback (light, medium, heavy)
 * @returns boolean - true if haptic was triggered, false otherwise
 */
export function triggerHaptic(type: HapticType = HapticType.LIGHT): boolean {
  // Check if haptics are supported
  if (!isHapticSupported()) {
    return false
  }

  // Check if haptics are enabled by user
  if (!isHapticEnabled()) {
    return false
  }

  // Only trigger on mobile devices
  if (!isMobileDevice()) {
    return false
  }

  // Get duration for the haptic type
  const duration = HAPTIC_DURATIONS[type]

  try {
    // Trigger vibration
    navigator.vibrate(duration)
    return true
  } catch (error) {
    console.warn('Haptic feedback failed:', error)
    return false
  }
}

/**
 * Trigger light haptic feedback (10ms)
 * Use for: light taps, hover effects
 */
export function hapticLight(): boolean {
  return triggerHaptic(HapticType.LIGHT)
}

/**
 * Trigger medium haptic feedback (20ms)
 * Use for: button clicks, navigation
 */
export function hapticMedium(): boolean {
  return triggerHaptic(HapticType.MEDIUM)
}

/**
 * Trigger heavy haptic feedback (30ms)
 * Use for: important actions, confirmations
 */
export function hapticHeavy(): boolean {
  return triggerHaptic(HapticType.HEAVY)
}

/**
 * Composable for using haptic feedback in Vue components
 */
export function useHaptics() {
  return {
    isSupported: isHapticSupported(),
    isEnabled: isHapticEnabled(),
    isMobile: isMobileDevice(),
    setEnabled: setHapticEnabled,
    trigger: triggerHaptic,
    light: hapticLight,
    medium: hapticMedium,
    heavy: hapticHeavy,
  }
}

